import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import HeaderV2 from '../components/HeaderV2';
import FooterV2 from '../components/FooterV2';
import LabPage from './LabPage';
import LabHeroV2 from './lab/v2/LabHeroV2';
import { LabCategoryKey } from './lab/v2/types';

/**
 * LabPageV2 — 做法 A：完全沿用舊版 LabPage 內容與排版，
 * 只替換為 HeaderV2 + FooterV2，並注入 V2 字型風格（EB Garamond + Noto Serif TC）。
 *
 * 換骨升級：在內嵌 LabPage 前加入 LabHeroV2（一張大圖 + 大字 + 左下角小麵包屑），
 * 與 ShopV2 / StoreV2 / WholesaleV2 等其他 V2 頁面 Hero 規格一致。
 *
 * 路由：/zh|en/lab/v2 與 /zh|en/lab/:category/v2（預覽用）
 *      未來可直接掛到 /zh|en/lab、/zh|en/lab/:category（正式接手）
 * 內容：分類 tabs、文章網格卡片、分享、SEO 結構皆沿用 LabPage，避免版面脫節。
 * SEO：依 pathname 是否含 '/v2' 動態判斷——
 *   - 預覽路由（含 /v2）：noindex,nofollow + canonical 指舊版 URL，避免重複內容
 *   - 正式路由（不含 /v2）：完全不寫 robots/canonical，讓內嵌 LabPage 自帶的 Helmet 設定生效
 */
const VALID_CATEGORIES: LabCategoryKey[] = ['all', 'news', 'guide', 'beanstory', 'events'];

const LabPageV2: React.FC = () => {
  const location = useLocation();
  const { category } = useParams<{ category?: string }>();
  const lang: 'zh' | 'en' = location.pathname.startsWith('/en') ? 'en' : 'zh';
  const isZh = lang === 'zh';
  const isPreviewRoute = location.pathname.includes('/v2');

  // 將 URL 中的 category 規範化為合法 LabCategoryKey，否則退回 'all'
  const heroCategory: LabCategoryKey =
    category && (VALID_CATEGORIES as string[]).includes(category)
      ? (category as LabCategoryKey)
      : 'all';

  // 注入 V2 共用字型（與其他 V2 頁面一致）
  useEffect(() => {
    const id = 'lab-v2-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500&family=Noto+Serif+TC:wght@400;500&display=swap';
    document.head.appendChild(link);
  }, []);

  const canonicalPath = `/${lang}/lab${category ? '/' + category : ''}`;

  return (
    <>
      {isPreviewRoute && (
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href={`https://www.ongucoffee.com${canonicalPath}`} />
        </Helmet>
      )}

      <div className="min-h-screen bg-white">
        <HeaderV2 />
        <LabHeroV2 category={heroCategory} isZh={isZh} />
        <LabPage />
        <FooterV2 />
      </div>
    </>
  );
};

export default LabPageV2;
